import type { RoundAggregate } from "@engine/round/aggregate";
import Matrix from "algorithm/matrix";
import { singleLayerSProcess } from "algorithm/singleLayerSProcess";
import Vector from "algorithm/vector";
import {
  getMatchingRecommenderLayerInputWithoutPreferenceLists,
  getRecommenderLayerInputUncached,
  getRecommenderPreferenceLists,
  STANDARD_RECOMMENDER_PHASE,
  type RecommenderLayerInput,
  type RecommenderLayerInputWithoutPreferenceLists,
  type RecommenderPhase,
} from "./recommenderPreferences";
import type {
  RecommenderBudgetPortion,
  RecommenderBudgetPortionSet,
} from "./recommenderBudgetPortions";

export type RecommenderLayerResult = {
  recommenderAccountsWithoutMatching: Matrix<"evaluator", "evaluable+hold">;
  recommenderGuaranteesWithoutMatching: Matrix<"evaluator", "evaluable+hold">;
  recommenderAccounts: Matrix<"evaluator", "evaluable+hold">;
  recommenderGuarantees: Matrix<"evaluator", "evaluable+hold">;
  phase2RecommenderLayerInput: RecommenderLayerInputWithoutPreferenceLists;
  remainingBudget: number;
};

const standardPreferenceListCache = new WeakMap<
  { [evaluableId: string]: number },
  Map<string, Matrix<"tranche", "proposal+hold">>
>();

/**
 * Fold an algorithm result with one row per budget portion back into one row
 * per real recommender. `parentRecommenderIndices` maps each portion row to its
 * destination in the real recommender ordering, so sibling synthetic portions
 * land in the same output row. Callers sum accounts because they are additive
 * dollars, but take the maximum guarantee because it is a shared endorsement
 * level rather than a separate amount for each portion.
 */
function aggregateBudgetPortionsByRecommender(
  budgetPortionMatrix: Matrix<"evaluator", "evaluable+hold">,
  parentRecommenderIndices: number[],
  numRecommenders: number,
  operation: "sum" | "max",
): Matrix<"evaluator", "evaluable+hold"> {
  const aggregated = Matrix.zeros<"evaluator", "evaluable+hold">(
    numRecommenders,
    budgetPortionMatrix.n_cols,
  );
  for (
    let budgetPortionIndex = 0;
    budgetPortionIndex < parentRecommenderIndices.length;
    budgetPortionIndex++
  ) {
    const parentRecommenderIndex = parentRecommenderIndices[budgetPortionIndex];
    if (parentRecommenderIndex == null) {
      continue;
    }
    for (let colIndex = 0; colIndex < budgetPortionMatrix.n_cols; colIndex++) {
      const current = aggregated.get(parentRecommenderIndex, colIndex);
      const next = budgetPortionMatrix.get(budgetPortionIndex, colIndex);
      aggregated.set(
        parentRecommenderIndex,
        colIndex,
        operation === "sum" ? current + next : Math.max(current, next),
      );
    }
  }
  return aggregated;
}

function getBudgetPortionPreferenceLists(
  round: RoundAggregate,
  recommenderLayerInput: RecommenderLayerInputWithoutPreferenceLists,
  budgetPortions: RecommenderBudgetPortion[],
  phase: RecommenderPhase,
): Matrix<"tranche", "proposal+hold">[] {
  if (phase.kind === "standard") {
    let byPortion = standardPreferenceListCache.get(
      recommenderLayerInput.proposalIndicesMap,
    );
    if (byPortion == null) {
      byPortion = new Map();
      standardPreferenceListCache.set(
        recommenderLayerInput.proposalIndicesMap,
        byPortion,
      );
    }
    return budgetPortions.map((budgetPortion) => {
      const key = `${budgetPortion.recommender.id}::${[
        ...budgetPortion.excludedProposalIds,
      ]
        .sort()
        .join(",")}`;
      const cached = byPortion.get(key);
      if (cached) {
        return cached;
      }
      const preferenceList = getRecommenderPreferenceLists(
        round,
        [budgetPortion],
        recommenderLayerInput.proposalMax,
        recommenderLayerInput.proposalIndicesMap,
        phase,
      )[0]!.rename("tranche -> tranche", "evaluable+hold -> proposal+hold");
      byPortion.set(key, preferenceList);
      return preferenceList;
    });
  }
  return getRecommenderPreferenceLists(
    round,
    budgetPortions,
    recommenderLayerInput.proposalMax,
    recommenderLayerInput.proposalIndicesMap,
    phase,
  ).map((matrix) =>
    matrix.rename("tranche -> tranche", "evaluable+hold -> proposal+hold"),
  );
}

/**
 * Run the standard and matching recommender phases for each independently
 * allocated portion of a recommender's budget. When funders have different
 * proposal exclusions, those portions come from splitting the recommender into
 * synthetic recommenders. The results are combined under the real recommender.
 */
export function runRecommenderLayer({
  round,
  phase1RecommenderLayerInput,
  budgetPortionSet,
  excludedProposalIds = {},
  debugLabelPrefix,
}: {
  round: RoundAggregate;
  phase1RecommenderLayerInput: RecommenderLayerInput;
  budgetPortionSet: RecommenderBudgetPortionSet;
  excludedProposalIds?: { [proposalId: string]: number };
  debugLabelPrefix: string;
}): RecommenderLayerResult {
  const budgetPortions = budgetPortionSet.portions;
  const usePrebuiltPreferenceLists =
    budgetPortionSet.kind === "one-per-recommender";
  const budgets = new Vector<"recommender">(
    budgetPortions.map((budgetPortion) => budgetPortion.budget),
  );
  const standardBudgets = new Vector<"recommender">(
    budgetPortions.map((budgetPortion) => budgetPortion.standardBudget),
  );
  const parentRecommenderIndices = budgetPortions.map(
    (budgetPortion) => budgetPortion.parentRecommenderIndex,
  );
  const numRecommenders =
    phase1RecommenderLayerInput.orderedRecommenders.length;

  const standardPreferenceLists = usePrebuiltPreferenceLists
    ? phase1RecommenderLayerInput.recommenderPreferenceLists
    : getBudgetPortionPreferenceLists(
        round,
        phase1RecommenderLayerInput,
        budgetPortions,
        STANDARD_RECOMMENDER_PHASE,
      );
  const standardPhaseOutput = singleLayerSProcess(
    standardBudgets.rename("recommender -> evaluator"),
    standardPreferenceLists.map((preferenceList) =>
      preferenceList.rename(
        "tranche -> tranche",
        "proposal+hold -> evaluable+hold",
      ),
    ),
    { debugLabel: `${debugLabelPrefix}:phase1` },
  );

  const standardGuaranteesByRecommender = aggregateBudgetPortionsByRecommender(
    standardPhaseOutput.guarantees,
    parentRecommenderIndices,
    numRecommenders,
    "max",
  );
  const matchingPhase: RecommenderPhase = {
    kind: "matching",
    initialGuarantees: standardGuaranteesByRecommender.rename(
      "evaluator -> recommender",
      "evaluable+hold -> evaluable+hold",
    ),
  };
  let phase2RecommenderLayerInput: RecommenderLayerInputWithoutPreferenceLists;
  let matchingPreferenceLists: Matrix<"tranche", "proposal+hold">[];
  if (usePrebuiltPreferenceLists) {
    const fullPhase2Input = getRecommenderLayerInputUncached(
      round,
      excludedProposalIds,
      matchingPhase,
    );
    phase2RecommenderLayerInput = fullPhase2Input;
    matchingPreferenceLists = fullPhase2Input.recommenderPreferenceLists;
  } else {
    phase2RecommenderLayerInput =
      getMatchingRecommenderLayerInputWithoutPreferenceLists(
        round,
        excludedProposalIds,
      );
    matchingPreferenceLists = getBudgetPortionPreferenceLists(
      round,
      phase2RecommenderLayerInput,
      budgetPortions,
      {
        kind: "matching",
        initialGuarantees: standardPhaseOutput.guarantees.rename(
          "evaluator -> recommender",
          "evaluable+hold -> evaluable+hold",
        ),
      },
    );
  }
  const matchingPhaseOutput = singleLayerSProcess(
    budgets.rename("recommender -> evaluator"),
    matchingPreferenceLists.map((preferenceList) =>
      preferenceList.rename(
        "tranche -> tranche",
        "proposal+hold -> evaluable+hold",
      ),
    ),
    { debugLabel: `${debugLabelPrefix}:phase2` },
  );

  return {
    recommenderAccountsWithoutMatching: aggregateBudgetPortionsByRecommender(
      standardPhaseOutput.accounts,
      parentRecommenderIndices,
      numRecommenders,
      "sum",
    ),
    recommenderGuaranteesWithoutMatching: aggregateBudgetPortionsByRecommender(
      standardPhaseOutput.guarantees,
      parentRecommenderIndices,
      numRecommenders,
      "max",
    ),
    recommenderAccounts: aggregateBudgetPortionsByRecommender(
      matchingPhaseOutput.accounts,
      parentRecommenderIndices,
      numRecommenders,
      "sum",
    ),
    recommenderGuarantees: aggregateBudgetPortionsByRecommender(
      matchingPhaseOutput.guarantees,
      parentRecommenderIndices,
      numRecommenders,
      "max",
    ),
    phase2RecommenderLayerInput,
    remainingBudget: matchingPhaseOutput.remainingBudget,
  };
}
